import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Chip } from "@mui/material";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ShareIcon from "@mui/icons-material/Share";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import IconButton from "@mui/material/IconButton";
import VerifiedIcon from "@mui/icons-material/Verified";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

const ReelCard = ({ reel, index,isActive,audioEnabled,onToggleAudio, onShopNow, rating }) => {
const videoRef = useRef(null);
const navigate = useNavigate();

  const handleShare = async () => {
    // Use product ID for the product details page URL (same as "Buy Me" button)
    const productId = reel.id || reel._id;
    const productUrl = `${window.location.origin}/product/${productId}`;
    
    // Create share text with product details
    const priceText = `₹${Number(reel.price).toLocaleString("en-IN")}`;
    const ratingText = reel.rating?.avg ? `⭐ ${reel.rating.avg.toFixed(1)}` : '';
    const shareText = `Check out "${reel.title}" ${priceText} ${ratingText}`.trim();
    
    const shareData = {
      title: reel.title,
      text: shareText,
      url: productUrl,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to copying URL to clipboard with a message
          const message = `${shareText}\n\n${productUrl}`;
          navigator.clipboard.writeText(message)
            .then(() => alert('Product details copied to clipboard! Paste to share.'))
            .catch(() => alert(`Share this product:\n\n${message}`));
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      const message = `${shareText}\n\n${productUrl}`;
      navigator.clipboard.writeText(message)
        .then(() => alert('Product details copied to clipboard! Paste to share.'))
        .catch(() => {
          const textArea = document.createElement('textarea');
          textArea.value = message;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Product details copied to clipboard! Paste to share.');
        });
    }
  };

  const handleBusinessNameClick = (e) => {
    e.stopPropagation();
    if (reel.vendorId) {
      navigate(`/store/${reel.vendorId}`);
    }
  };

  useEffect(() => {
  const video = videoRef.current;
  if (!video) return;

  if (isActive) {
    video.muted = !audioEnabled;

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {});
    }
  } else {
    video.pause();
    video.currentTime = 0;
    video.muted = true;
  }
}, [isActive, audioEnabled]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: 2,
        backgroundColor: "#000",
      }}
    >
      {reel.videoUrl && isActive && (
  <IconButton
    onClick={(e) => {
      e.stopPropagation(); // prevent swiper / unlock tap
      onToggleAudio();
    }}
    sx={{
      position: "absolute",
      top: 48,
      right: 16,
      zIndex: 10,
      backgroundColor: "rgba(0,0,0,0.6)",
      color: "#fff",
      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.8)",
      },
    }}
  >
    {audioEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
  </IconButton>
)}

      {/* Share Button */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleShare();
        }}
        sx={{
          position: "absolute",
          top: 100,
          right: 16,
          zIndex: 10,
          backgroundColor: "rgba(0,0,0,0.6)",
          color: "#fff",
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.8)",
          },
        }}
      >
        <ShareIcon />
      </IconButton>

      {/* Wishlist Button */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          // TODO: Add wishlist functionality
        }}
        sx={{
          position: "absolute",
          top: 152,
          right: 16,
          zIndex: 10,
          backgroundColor: "rgba(0,0,0,0.6)",
          color: "#fff",
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.8)",
          },
        }}
      >
        <FavoriteBorderIcon />
      </IconButton>

      {/* Video / Image */}
      {reel.videoUrl ? (
        
        <video
        ref={videoRef}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          muted
          loop
          // autoPlay
          playsInline
          preload="metadata"
          poster={reel.imageUrl}
        >
          <source src={reel.videoUrl} type="video/mp4" />
        </video>
      ) : (
        <img
          src={reel.imageUrl}
          alt={reel.title}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      {/* Gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "25%",
          background:
            "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.1))",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "absolute",
          bottom: `calc(16px + env(safe-area-inset-bottom))`,
          left: 20,
          right: 20,
          color: "white",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.3)",
              backgroundColor: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {reel.vendorImage ? (
              <img
                src={reel.vendorImage}
                alt={reel.businessName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Typography variant="caption" sx={{ fontSize: 12, color: "white", fontWeight: 600 }}>
                V
              </Typography>
            )}
          </Box>
          <Typography
            variant="subtitle2"
            onClick={handleBusinessNameClick}
            sx={{
              fontWeight: 500,
              color: "white",
              fontSize: "0.9rem",
              letterSpacing: "0.2px",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
                opacity: 0.9
              }
            }}
          >
            {reel.businessName}
          </Typography>
          {/* Sponsored Badge */}
          {reel.sponsorshipStatus === "ACTIVE" && (
            <Chip
              icon={<WorkspacePremiumIcon />}
              label="Sponsored"
              size="small"
              sx={{
                bgcolor: "#fff4e5",
                color: "#b26a00",
                fontWeight: 600,
                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                height: 20,
                "& .MuiChip-icon": {
                  fontSize: { xs: 12, sm: 14 },
                }
              }}
            />
          )}
          {/* {reel.isVerified && (
            <VerifiedIcon sx={{ fontSize: 14, color: "#00BFFF", display: "block" }} />
          )} */}
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 400,
            color: "white",
            fontSize: "1rem",
            lineHeight: 1.4,
            textShadow: "0px 1px 3px rgba(0,0,0,0.5)"
          }}
        >
          {reel.title}
        </Typography>

        {/* Rating */}
        {(reel.rating?.avg !== undefined || rating?.avg !== undefined || reel.rating || reel.rating === 0) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <Typography
              variant="caption"
              sx={{ color: "#FFD700", fontWeight: 600, fontSize: "0.8rem", display: "flex", alignItems: "center" }}
            >
              ⭐ {reel.rating?.avg !== undefined ? reel.rating.avg.toFixed(1) : (rating?.avg !== undefined ? rating.avg.toFixed(1) : reel.rating.toFixed(1))}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
            >
              ({reel.rating?.count || rating?.count || reel.ratingCount || 0})
            </Typography>
          </Box>
        )}

      {/* <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1 }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(reel.description),
        }}
      /> */}

        <Box
          sx={{
            mt: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: "rgba(255,255,255,0.9)",
              fontSize: "0.95rem"
            }}
          >
            ₹ {Number(reel.price).toLocaleString("en-IN")}
          </Typography>

          <Button
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onShopNow();
            }}
            sx={{
              backgroundColor: "#FFD814",
              color: "black",
              textTransform: "none",
              borderRadius: "100px",
              px: 3,
              py: 0.8,
              fontSize: "0.85rem",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#e6c512" },
            }}
          >
            Buy Me
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ReelCard;
